import useShadowDocument from '@payaca/components/shadowDocument/useShadowDocument';
import { DOCUMENT_REGION_ATTRIBUTES } from '@payaca/document-rendering/regions';
import { scrollBehaviour } from '@payaca/utilities/motionUtilities';
import { useEffect, useRef } from 'react';

/**
 * The region of the rendered document a click landed in, resolved to what the
 * builder can open for editing.
 */
export type ProposalDocumentRegion =
  | { type: 'section'; sectionKey: string }
  | { type: 'lineItem'; sectionKey: string; proposalLineItemId: string };

const { sectionKey: SECTION_ATTRIBUTE, lineItemId: LINE_ITEM_ATTRIBUTE } =
  DOCUMENT_REGION_ATTRIBUTES;

/** Stops the rendered document behaving like a live document for the customer. */
const INERT_STYLES = `
  :host { pointer-events: none; }
`;

/**
 * Builder-only styles that turn the preview into a navigation control.
 *
 * Line items are listed as well as their section, though both properties
 * inherit: a selectable item is a `<label>` for a control the document renders
 * disabled, and the browser gives such a label its own `cursor`, which beats
 * the inherited one. Groups need no rule — they are plain elements.
 */
const INTERACTIVE_REGION_STYLES = `
  [${SECTION_ATTRIBUTE}], [${LINE_ITEM_ATTRIBUTE}] {
    pointer-events: auto;
    cursor: pointer;
  }
`;

/**
 * The template nests a line item inside its group, and the group inside its
 * section's wrapper, so one pass over the path resolves both halves of a line
 * item's edit route.
 */
const regionFromPath = (path: EventTarget[]): ProposalDocumentRegion | null => {
  let proposalLineItemId: string | null = null;

  for (const node of path) {
    if (!(node instanceof HTMLElement)) continue;

    proposalLineItemId ??= node.getAttribute(LINE_ITEM_ATTRIBUTE) || null;

    const sectionKey = node.getAttribute(SECTION_ATTRIBUTE) || null;
    if (sectionKey) {
      return proposalLineItemId
        ? { type: 'lineItem', sectionKey, proposalLineItemId }
        : { type: 'section', sectionKey };
    }
  }

  return null;
};

type UseProposalDocumentPreviewArgs = {
  /** The server-rendered document, markup and stylesheet together. */
  html: string;
  /**
   * Called with the region a click resolved to. Omit to render the document
   * without builder interactivity.
   */
  onRegionClick?: (region: ProposalDocumentRegion) => void;
  /**
   * The section being edited. Its region is scrolled into view when it changes,
   * so choosing a tab moves the preview to the matching part of the document.
   */
  activeSectionKey?: string | null;
};

/**
 * Render a server-generated proposal document into a Shadow DOM, and make its
 * regions open the matching editor.
 */
const useProposalDocumentPreview = (args: UseProposalDocumentPreviewArgs) => {
  const { html, onRegionClick, activeSectionKey } = args;

  const { hostRef, shadowRootRef, hostAttached } = useShadowDocument({
    html,
    styleSheets: onRegionClick
      ? [INERT_STYLES, INTERACTIVE_REGION_STYLES]
      : [INERT_STYLES],
  });

  // The click listener goes on the host, which is outside the shadow tree, so
  // a click is a composed event by the time it arrives and `composedPath()`
  // sees the tagged ancestors that `closest()` could not.
  useEffect(() => {
    const shadowRoot = shadowRootRef.current;
    if (!shadowRoot || !onRegionClick) return;

    const host = shadowRoot.host;
    const handleClick = (event: Event) => {
      // A selectable item card is a `<label>` for the Customer's selection
      // control, so an uncancelled click would toggle which items the Proposal
      // has selected by default. Here the Sales Agent is editing, not
      // selecting. This also stops a link in the document navigating away.
      event.preventDefault();

      const region = regionFromPath(event.composedPath());
      if (region) onRegionClick(region);
    };
    host.addEventListener('click', handleClick);

    return () => host.removeEventListener('click', handleClick);
  }, [shadowRootRef, hostAttached, onRegionClick]);

  // Scroll on a change of section, but not on arrival — the preview should open
  // at the top of the document rather than jumping to the first tab's section.
  // Whichever of the document and the tabs resolves first, arriving at a
  // section from nothing is not a change.
  const lastSectionKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const shadowRoot = shadowRootRef.current;
    if (!shadowRoot) return;

    const previousSectionKey = lastSectionKeyRef.current;
    lastSectionKeyRef.current = activeSectionKey ?? null;

    if (!activeSectionKey) return;
    if (!previousSectionKey || previousSectionKey === activeSectionKey) return;

    shadowRoot
      .querySelector(`[${SECTION_ATTRIBUTE}="${CSS.escape(activeSectionKey)}"]`)
      ?.scrollIntoView({ behavior: scrollBehaviour(), block: 'start' });
  }, [activeSectionKey, shadowRootRef, hostAttached]);

  return hostRef;
};

export default useProposalDocumentPreview;
